import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppSettings } from './app.settings';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { NgxSpinnerModule } from 'ngx-spinner';
import { SharedModule } from './shared/shared.module';
import { LoadingService } from './core/services/loading.service';
import { AppInterceptor } from './core/interceptors/app-interceptor';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { CustomOverlayContainer } from './shared/utils/custom-overlay-container';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { menuScrollStrategy } from './shared/utils/scroll-strategy';
import { CoreModule } from './core/core.module';

const config = require('./app-config.json');

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.debug('client logging' + message);
}

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: config.auth.authority,
      redirectUri: window.location.origin + environment.baseHref,
      postLogoutRedirectUri: window.location.origin + environment.baseHref + '/landing',
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: true,
      },
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  // strictMatching (default since msal-angular v6) requires an explicit trailing
  // wildcard to match sub-paths; `*` with no preceding slash also matches the bare
  // base URL itself, so these patterns cover both the root and any sub-path.
  protectedResourceMap.set(`${environment.webApi}`, [environment.apiScopeUrl]);
  protectedResourceMap.set(`${environment.webApi}/*`, [environment.apiScopeUrl]);
  protectedResourceMap.set(`${config.resources.graphApi.resourceUri}`, [
    config.resources.graphApi.resourceScope,
  ]);
  protectedResourceMap.set(`${config.resources.graphApi.resourceUri}/*`, [
    config.resources.graphApi.resourceScope,
  ]);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
  ]);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me/*', [
    'user.read',
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  };
}

// When auth is disabled (environment.noauth.ts) no MSAL redirect handling and no bearer tokens are needed
const msalInterceptorProviders = environment.authEnabled
    ? [{ provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true }]
    : [];

@NgModule({ declarations: [AppComponent],
    bootstrap: environment.authEnabled ? [AppComponent, MsalRedirectComponent] : [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        NgxSpinnerModule,
        SharedModule,
        CoreModule,
        BrowserAnimationsModule], providers: [
        [LoadingService],
        ...msalInterceptorProviders,
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        { provide: OverlayContainer, useClass: CustomOverlayContainer },
        {
            provide: MAT_MENU_SCROLL_STRATEGY,
            useFactory: menuScrollStrategy,
            deps: [Overlay],
        },
        { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
        MsalService,
        MsalGuard,
        AppSettings,
        MsalBroadcastService,
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ] })
export class AppModule {}
